import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FactoryProvider } from "./contexts/FactoryContext";
import Layout from "./components/Layout";
import { ScrollRestoration } from "./hooks/useScrollRestoration";
import SplashScreen from "./components/SplashScreen";
import SuperAdminLayout from "./components/SuperAdminLayout";
// LoginPage stays eager — it's the entry screen for unauthenticated users, so
// keeping it in the initial bundle makes first paint fast. Everything else is
// route-level code-split via React.lazy so each page's JS (and the heavy libs
// it pulls in, e.g. recharts / framer-motion) only loads when that route opens.
import LoginPage from "./pages/LoginPage";
const SignupPage = lazy(() => import("./pages/SignupPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DesignGallery = lazy(() => import("./pages/DesignGallery"));
const MasterStylesPage = lazy(() => import("./pages/MasterStylesPage"));
const PurchaseOrdersPage = lazy(() => import("./pages/PurchaseOrdersPage"));
const POCompletionPage = lazy(() => import("./pages/POCompletionPage"));
const CuttingReportsPage = lazy(() => import("./pages/CuttingReportsPage"));
const PickStylePage = lazy(() => import("./pages/cutting/PickStylePage"));
const TablesSetupPage = lazy(() => import("./pages/cutting/TablesSetupPage"));
const CuttingDonePage = lazy(() => import("./pages/cutting/CuttingDonePage"));
const CuttingAnalysisPage = lazy(() => import("./pages/cutting/CuttingAnalysisPage"));
const ContractorsPage = lazy(() => import("./pages/ContractorsPage"));
const LotsPage = lazy(() => import("./pages/LotsPage"));
const ProductionPage = lazy(() => import("./pages/ProductionPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const LedgersPage = lazy(() => import("./pages/LedgersPage"));
const RateMasterPage = lazy(() => import("./pages/RateMasterPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SamplingPage = lazy(() => import("./pages/SamplingPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const CostMasterPage = lazy(() => import("./pages/CostMasterPage"));
const CostingsListPage = lazy(() => import("./pages/CostingsListPage"));
const CostingPickerPage = lazy(() => import("./pages/CostingPickerPage"));
const CostingEditorPage = lazy(() => import("./pages/CostingEditorPage"));
const CostingReportsPage = lazy(() => import("./pages/CostingReportsPage"));
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"));
const ContractorPortalPage = lazy(() => import("./pages/ContractorPortalPage"));
const PackingPage = lazy(() => import("./pages/PackingPage"));
const SuperAdminDashboard = lazy(() => import("./pages/super-admin/SuperAdminDashboard"));
const SuperAdminCompanies = lazy(() => import("./pages/super-admin/SuperAdminCompanies"));
const SuperAdminSubscriptions = lazy(() => import("./pages/super-admin/SuperAdminSubscriptions"));
const SuperAdminRevenue = lazy(() => import("./pages/super-admin/SuperAdminRevenue"));
const SuperAdminSupport = lazy(() => import("./pages/super-admin/SuperAdminSupport"));
const SuperAdminSettings = lazy(() => import("./pages/super-admin/SuperAdminSettings"));
const SuperAdminTraining = lazy(() => import("./pages/super-admin/SuperAdminTraining"));
const SubscriptionBlocked = lazy(() => import("./pages/SubscriptionBlocked"));
import { hasViewAccess } from "./components/Layout";
import UpgradeModal from "./components/UpgradeModal";
import { ThemeProvider } from "./lib/theme";

/**
 * Tenant-only route wrapper.
 *
 * Super-admins are HARD-redirected to /super-admin/* and CANNOT access
 * factory operation routes (dashboard, lots, master-styles, finance, etc.)
 * unless they explicitly start an "Login as Company" impersonation session.
 *
 * `permKey` is checked against the user's view permissions for non-super users.
 */
function ProtectedRoute({ children, permKey }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isImpersonating, setIsImpersonating] = React.useState(null);

  const refresh = React.useCallback(async () => {
    // Only flip state once `user` is actually loaded — otherwise we'd
    // momentarily set isImpersonating=false during initial auth load,
    // which races the Navigate-to-/super-admin redirect for super-admins.
    if (!user) return;
    if (user.role === 'super_admin') {
      try {
        const r = await (await import('./lib/api')).default.get('/admin/impersonation-state');
        setIsImpersonating(!!r.data?.impersonating);
      } catch { setIsImpersonating(false); }
    } else {
      setIsImpersonating(false);
    }
  }, [user]);

  React.useEffect(() => { refresh(); }, [refresh, location.pathname]);

  // React when impersonation toggles (e.g., super-admin clicks Impersonate
  // in another tab/window) so the gate becomes accurate immediately.
  // iter102: The previous version listened to every `focus` event, which on
  // mobile fires on EVERY keyboard pop-up / address-bar tap → an API call
  // per keystroke and constant re-renders. Now we only listen to the
  // explicit cross-tab event + a slow 5-min poll for super-admins.
  React.useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('impersonation-changed', handler);
    let interval = null;
    if (user && user.role === 'super_admin') {
      interval = setInterval(refresh, 5 * 60 * 1000);
    }
    return () => {
      window.removeEventListener('impersonation-changed', handler);
      if (interval) clearInterval(interval);
    };
  }, [refresh, user]);

  if (loading || (user && user.role === 'super_admin' && isImpersonating === null)) return <SplashScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // Super-admin without an active impersonation session must stay inside the
  // SaaS console — they should not see factory operation modules.
  if (user.role === 'super_admin' && !isImpersonating) {
    return <Navigate to="/super-admin" replace />;
  }
  if (permKey && !hasViewAccess(user, permKey)) return <Navigate to="/dashboard" replace />;
  return children;
}

function SuperAdminProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <SplashScreen subtitle="Opening SaaS console…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/subscription-blocked" element={<SubscriptionBlocked />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Layout><BillingPage /></Layout></ProtectedRoute>} />

      {/* Super-admin SaaS console — fully separate shell */}
      <Route path="/super-admin" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminDashboard /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/companies" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminCompanies /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/subscriptions" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminSubscriptions /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/revenue" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminRevenue /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/support" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminSupport /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/settings" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminSettings /></SuperAdminLayout></SuperAdminProtectedRoute>} />
      <Route path="/super-admin/training" element={<SuperAdminProtectedRoute><SuperAdminLayout><SuperAdminTraining /></SuperAdminLayout></SuperAdminProtectedRoute>} />

      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/design-gallery" element={<ProtectedRoute><Layout><DesignGallery /></Layout></ProtectedRoute>} />
      <Route path="/master-styles" element={<ProtectedRoute permKey="/master-styles"><Layout><MasterStylesPage /></Layout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute permKey="/purchase-orders"><Layout><PurchaseOrdersPage /></Layout></ProtectedRoute>} />
      <Route path="/po-completion" element={<ProtectedRoute permKey="/purchase-orders"><Layout><POCompletionPage /></Layout></ProtectedRoute>} />
      <Route path="/demand" element={<Navigate to="/cutting/analysis" replace />} />
      <Route path="/cutting" element={<Navigate to="/cutting/pick-style" replace />} />
      <Route path="/cutting/pick-style" element={<ProtectedRoute permKey="/cutting"><Layout><PickStylePage /></Layout></ProtectedRoute>} />
      <Route path="/cutting/tables" element={<ProtectedRoute permKey="/cutting"><Layout><TablesSetupPage /></Layout></ProtectedRoute>} />
      <Route path="/cutting/done" element={<ProtectedRoute permKey="/cutting"><Layout><CuttingDonePage /></Layout></ProtectedRoute>} />
      <Route path="/cutting/analysis" element={<ProtectedRoute permKey="/cutting"><Layout><CuttingAnalysisPage /></Layout></ProtectedRoute>} />
      <Route path="/sampling" element={<ProtectedRoute permKey="/sampling"><Layout><SamplingPage /></Layout></ProtectedRoute>} />
      <Route path="/contractors" element={<ProtectedRoute permKey="/contractors"><Layout><ContractorsPage /></Layout></ProtectedRoute>} />
      <Route path="/lots" element={<ProtectedRoute permKey="/lots"><Layout><LotsPage /></Layout></ProtectedRoute>} />
      <Route path="/production" element={<ProtectedRoute permKey="/production"><Layout><ProductionPage /></Layout></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute permKey="/invoices"><Layout><InvoicesPage /></Layout></ProtectedRoute>} />
      <Route path="/ledgers" element={<ProtectedRoute permKey="/ledgers"><Layout><LedgersPage /></Layout></ProtectedRoute>} />
      <Route path="/rate-master" element={<ProtectedRoute permKey="/rate-master"><Layout><RateMasterPage /></Layout></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute permKey="/alerts"><Layout><AlertsPage /></Layout></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Layout><SupportPage /></Layout></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Layout><TrainingPage /></Layout></ProtectedRoute>} />
      <Route path="/cost-master" element={<Navigate to="/materials?tab=master" replace />} />
      <Route path="/costings" element={<ProtectedRoute permKey="/costings"><Layout><CostingsListPage /></Layout></ProtectedRoute>} />
      <Route path="/costings/reports" element={<ProtectedRoute permKey="/costings"><Layout><CostingReportsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-contractor" element={<ProtectedRoute><Layout><ContractorPortalPage /></Layout></ProtectedRoute>} />
      <Route path="/packing" element={<ProtectedRoute permKey="/packing"><Layout><PackingPage /></Layout></ProtectedRoute>} />
      <Route path="/costings/new" element={<ProtectedRoute permKey="/costings"><Layout><CostingPickerPage /></Layout></ProtectedRoute>} />
      <Route path="/costings/:id" element={<ProtectedRoute permKey="/costings"><Layout><CostingEditorPage /></Layout></ProtectedRoute>} />
      <Route path="/materials" element={<ProtectedRoute permKey="/materials"><Layout><MaterialsPage /></Layout></ProtectedRoute>} />
      <Route path="/costing-clients" element={<Navigate to="/settings?tab=presets" replace />} />
      <Route path="/settings" element={<ProtectedRoute permKey="/settings"><Layout><SettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/" element={<RoleBasedHome />} />
      <Route path="*" element={<RoleBasedHome />} />
    </Routes>
  );
}

function RoleBasedHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FactoryProvider>
            <ScrollRestoration />
            {/* Outer boundary catches lazy routes that render WITHOUT the app
                shell (login-adjacent screens like Signup / Onboarding). Routes
                rendered inside Layout / SuperAdminLayout resolve at the inner
                Suspense in those shells, so the shell never unmounts. */}
            <Suspense fallback={<SplashScreen />}>
              <AppRouter />
            </Suspense>
            <UpgradeModal />
            <Toaster position="top-right" richColors />
          </FactoryProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
